import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import styles from './PersonalInformation.module.css';
import { t } from 'i18next';
import { EditIcon } from '../../../../assets/images/icons'; // Replace with correct import for the icon
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import FormField from '../../../../components/UI/FormUI/FormField/FormField';
import FormDateField from '../../../../components/UI/FormUI/FormDateField/FormDateField';
import FormSelectField from '../../../../components/UI/FormUI/FormSelectField/FormSelectField';
import FormOptionField from '../../../../components/UI/FormUI/FormOptionField/FormOptionField';

import Subtitle from '../../../../components/UI/Common/Subtitle/Subtitle';
import LanguageLevelList from '../../../../components/UI/Common/LanguageLevel/LanguageLevelList/LanguageLevelList';
import BasicPopup from '../../../../components/UI/Popups/BasicPopup/BasicPopup';
import LanguageForm from '../../../../components/Forms/Common/LanguageForm/LanguageForm';
import { updateUser } from '../../../../services/userService';
import toast from 'react-hot-toast';
import { fetchUserByToken, fetchUserByTokenWithoutLoading, updateUserProperty } from '../../../../app/features/userSlice';
import { formatDateForMySQL } from '../../../../utils/formatHelpers';
import Button from '../../Buttons/Button/Button';
import { useNavigate } from 'react-router-dom';
import { Close } from '@mui/icons-material';
import { useGetCitiesOfState, useGetStates } from '../../../../utils/utilityHooks';
import FormFileField from '../../FormUI/FormFileField/FormFileField';
import LogicTestBar from '../LogicTestBar/LogicTestBar';
import LogicTestForm from '../../../Forms/Common/LogicTestForm/LogicTestForm';
import { getQuestions } from '../../../../services/logicTestService';

function PersonalInformation({ jobseeker, activeTab }) {
    const user = useSelector(state => state.user.user);
    const [isOpenLanguagePopup, setIsOpenLanguagePopup] = useState(false); // To manage the language popup visibility
    const [formLoading, setFormLoading] = useState(false);
    const [editMode, setEditMode] = useState(true)
    const [isOpenLogicTestPopup, setIsOpenLogicTestPopup] = useState(false);
    const [showTest, setShowTest] = useState(false);
    const [testOptions, setTestOptions] = useState([]);
    const [isTestLoading, setIsTestLoading] = useState(false);
    const [answersToken, setAnswersToken] = useState('');

    const language = useSelector((state) => state.translation.language);
    const userToken = useSelector(state => state.user.token);
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const formik = useFormik({
        initialValues: {
            first_name: jobseeker?.first_name || '',
            last_name: jobseeker?.last_name || '',
            birth_date: jobseeker?.birth_date || '',
            gender: jobseeker?.gender || '',
            nationality: jobseeker?.nationality || '',
            marital_status: jobseeker?.marital_status || '',
            languages: jobseeker?.languages || [],
            driving_license: jobseeker.driving_license || '',
        },
        validationSchema: Yup.object({
            first_name: Yup.string().required(t('general.UI.required')),
            last_name: Yup.string().required(t('general.UI.required')),
            birth_date: Yup.string().required(t('general.UI.required')),
            gender: Yup.string().required(t('general.UI.required')),
            nationality: Yup.string().required(t('general.UI.required')),
            marital_status: Yup.string().required(t('forms.welcome_job_seeker.step_1.required')),
            driving_license: Yup.mixed()
                .test(
                    'is-valid-driving-license',
                    t('forms.welcome_job_seeker.step_1.unsupported_file_format'),
                    (value) => typeof value === 'string' || !value || true
                )
                .test(
                    'file-size',
                    t('forms.welcome_job_seeker.step_1.file_size_must_be_less_than_5MB'),
                    (value) => typeof value === 'string' || !value || (value?.size <= 1024 * 1024 * 5)
                )
                .test(
                    'file-type',
                    t('forms.welcome_job_seeker.step_1.unsupported_file_format'),
                    (value) => typeof value === 'string' || !value || ['application/pdf', 'application/msword',
                        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                        'application/vnd.oasis.opendocument.text'
                    ].includes(value?.type)
                ),
        }),
        onSubmit: (values) => {
            if (formLoading) {
                return;
            }

            setFormLoading(true);

            const formData = new FormData();
            formData.append('token', userToken); // Assuming you have user token
            formData.append('first_name', values.first_name);
            formData.append('last_name', values.last_name);
            formData.append('gender', values.gender);
            formData.append('nationality', values.nationality);
            formData.append('birth_date', formatDateForMySQL(values.birth_date));
            formData.append('marital_status', values.marital_status);

            if (values.driving_license) {
                formData.append('driving_license', values.driving_license);
            }

            if (values?.languages?.length > 0) {
                values?.languages.forEach((language, index) => {
                    formData.append(`languages[${index}][languageCode]`, language.languageCode);
                    formData.append(`languages[${index}][level]`, language.level);
                });
            }
            else {
                formData.append('languages', '');
            }

            const target = document.querySelector('.personalInformation')
            if (target) {
                window.scrollTo({
                    top: target.offsetTop,
                    behavior: 'smooth'
                });
            }

            toast.promise(updateUser(formData), {
                loading: t('edit_profile.updating_profile'),
                success: <b>{t('edit_profile.profile_updated')}</b>,
                error: (err) => <b>{err.response.data.error}</b>,
            }).then(async () => {
                await dispatch(fetchUserByTokenWithoutLoading(userToken));
                await setEditMode(!editMode)
                await setFormLoading(false);
            })
                .catch((error) => {
                    setFormLoading(false);
                    if (error?.response?.data?.field) {
                        formik.setErrors({
                            [error.response.data.field]: error.response.data.error
                        });
                    }
                });
        },
    });

    const citiesOptions = useGetCitiesOfState(formik.values.state);
    const statesOptions = useGetStates(formik.values.country);

    const genderOptions = [
        { value: 'male', label: t('forms.welcome_job_seeker.step_1.male') },
        { value: 'female', label: t('forms.welcome_job_seeker.step_1.female') }
    ];

    const maritalStatusOptions = [
        { value: 'married', label: t('forms.welcome_job_seeker.step_1.married') },
        { value: 'unmarried', label: t('forms.welcome_job_seeker.step_1.unmarried') }
    ];

    const visaStatus = [
        { value: '', label: t('forms.welcome_job_seeker.step_1.none') },
        { value: 'valid', label: t('forms.welcome_job_seeker.step_1.valid') },
        { value: 'expired', label: t('forms.welcome_job_seeker.step_1.expired') }
    ];

    let isLogicMoreThan30Days = true;
    if (user.logic_test_request_date) {
        const logicTestRequestDate = new Date(user.logic_test_request_date);

        const currentDate = new Date();
        const thirtyDaysAgo = new Date(currentDate.setDate(currentDate.getDate() - 30));

        isLogicMoreThan30Days = logicTestRequestDate < thirtyDaysAgo;
    }

    const handlePopupClose = (popupName) => {
        if (popupName === 'logicTest') {
            setIsOpenLogicTestPopup(false);
        }
    }

    const handlePopupOpen = (popupName) => {
        if (popupName === 'logicTest') {
            setIsOpenLogicTestPopup(true);
        }
    }

    const handleStartTest = () => {
        setIsTestLoading(true);

        toast.promise(getQuestions({ token: userToken, language: language }), {
            loading: t('forms.welcome_job_seeker.step_3.generationg_test'),
            success: <b>{t('forms.welcome_job_seeker.step_3.test_ready')}</b>,
            error: (err) => {
                return <b>{err.response.data.error}</b>;
            },
        })
            .then((data) => {
                let questions = [];
                if (data.questions) {

                    if (data.questions.questions) {
                        questions = data.questions.questions.map((question, index) => {
                            return {
                                id: index,
                                question: question.question,
                                answers: Object.entries(question.answers).map(([key, value]) => {
                                    return { label: value, value: key };
                                })
                            }
                        });
                    }
                    else {
                        questions = data.questions.map((question, index) => {
                            return {
                                id: index,
                                question: question.question,
                                answers: Object.entries(question.answers).map(([key, value]) => {
                                    return { label: value, value: key };
                                })
                            }
                        });
                    }

                    setAnswersToken(data.answers_token);
                    setTestOptions(questions);

                    setShowTest(true);
                    setIsTestLoading(false);
                }

            })
            .catch((error) => {
                toast.error(error.response.data.error);
                setIsTestLoading(false);
            });

    }

    return (
        <div className={styles.container}>
            {/* Second Column */}
            <div className={styles.secondColumn}>
                {!editMode &&
                    <Close
                        className={styles.mobileIcon}
                        style={{
                            cursor: "pointer"
                        }}
                        onClick={() => { setEditMode(!editMode) }}
                    />
                }
                <div className={`${styles.title} personalInformation`}>{t('personalInformation.title')}</div>
                {editMode ?
                    <>
                        <div className={styles.aboutTitle}>{t('personalInformation.name')}</div>
                        <div className={styles.description}>
                            {jobseeker?.first_name} {jobseeker?.last_name}
                        </div>
                    </>
                    :
                    <>
                        <div className={styles.aboutTitle}></div>

                        <div className={styles.editContainer}>
                            <FormField
                                name="first_name"
                                label={t('forms.welcome_job_seeker.step_1.first_name')}
                                hasBorder
                                value={formik.values.first_name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.first_name && formik.errors.first_name}
                            />
                            <FormField
                                name="last_name"
                                label={t('forms.welcome_job_seeker.step_1.last_name')}
                                hasBorder
                                value={formik.values.last_name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.last_name && formik.errors.last_name}
                            />
                        </div>
                    </>
                }

                {editMode ?
                    <>
                        <div className={styles.aboutTitle}>{t('personalInformation.dateOfBirth')}</div>
                        <div className={styles.description}>
                            {jobseeker?.birth_date && `${moment(jobseeker?.birth_date).format('MMM DD, YYYY')}, ${moment().diff(moment(jobseeker?.birth_date), 'years')} ${t('job_seeker.years_old')}`}
                        </div>
                    </>
                    :
                    <div className={styles.editContainer}>
                        <FormDateField
                            name="birth_date"
                            label={t('edit_profile.job_seeker.birthdate')}
                            hasBorder
                            value={formik.values.birth_date}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            error={formik.touched.birth_date && formik.errors.birth_date}
                        />
                    </div>
                }
                {editMode ?
                    <>
                        <div className={styles.aboutTitle}>{t('personalInformation.gender')}</div>
                        <div className={styles.description}>
                            {jobseeker?.gender && jobseeker?.gender}
                        </div>
                    </> :
                    <div className={styles.editContainer}>
                        <FormOptionField
                            name="gender"
                            label={t('edit_profile.job_seeker.gender')}
                            type="radio"
                            dark
                            options={genderOptions}
                            onChange={formik.handleChange}
                            value={formik.values.gender}
                            error={formik.touched.gender && formik.errors.gender}
                        />
                    </div>}

                {editMode ?
                    <>
                        <div className={styles.aboutTitle}>{t('personalInformation.nationality')}</div>
                        <div className={styles.description}>
                            {jobseeker?.nationality && t('general.nationality.' + jobseeker?.nationality)}
                        </div>
                    </> :

                    <div className={styles.editContainer}>
                        <FormSelectField
                            name="nationality"
                            type="nationality"
                            label={t('personalInformation.nationality')}
                            hasBorder
                            onFormikChange={formik.handleChange}
                            value={formik.values.nationality}
                            error={formik.touched.nationality && formik.errors.nationality}
                        />
                    </div>}

                {editMode ?
                    <>
                    </>
                    :
                    <>
                        <div>
                            <FormSelectField
                                name="marital_status"
                                hasBorder
                                type="default"
                                placeholder={t('forms.welcome_job_seeker.step_1.marital_status')}
                                label={t('forms.welcome_job_seeker.step_1.marital_status')}
                                options={maritalStatusOptions}
                                onFormikChange={formik.handleChange}
                                value={formik.values.marital_status}
                                error={formik.touched.marital_status && formik.errors.marital_status}
                            />
                        </div>
                        <div>
                            <FormFileField
                                name="driving_license"
                                accept=".pdf,.doc,.docx,.odt"
                                type="file"
                                placeholder={t('forms.welcome_job_seeker.step_1.driving_license')}
                                label={t('forms.welcome_job_seeker.step_1.driving_license')}
                                hasBorder
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.driving_license}
                                error={formik.touched.driving_license && formik.errors.driving_license}
                            />
                        </div>
                    </>
                }
                {editMode ?

                    <>
                        {jobseeker.driving_license &&
                            <>
                                <div className={styles.aboutTitle}>{t('forms.welcome_job_seeker.step_1.driving_license')}</div>
                                <div className={styles.description}>
                                    <a
                                        href={process.env.REACT_APP_UPLOADS_PATH + jobseeker.driving_license}
                                        target="_blank"
                                        className={styles.description}
                                        rel="noopener noreferrer"
                                    >
                                        {t('overview.viewDrivingLicense')}
                                    </a>
                                </div>
                            </>
                        }
                        {jobseeker?.languages?.length > 0 &&
                            <>

                                <div className={styles.aboutTitle}>{t('personalInformation.language')}</div>
                                <div className={styles.description}>
                                    {jobseeker?.languages?.map((lang, index) => {
                                        return (
                                            <span key={index}>
                                                {t(`general.language.${lang.languageCode}`)}
                                                {lang.level > 4 && " (" + t('general.fluent') + ")"}
                                                {index < jobseeker?.languages?.length - 1 && ', '}

                                            </span>
                                        )
                                    })}
                                </div>
                            </>}
                    </>
                    :
                    <div className={styles.editContainer}>
                        {/* <Subtitle dark>{t('general.UI.languages')}</Subtitle> */}
                        <Subtitle
                            dark
                            hasButton
                            buttonText={t('general.UI.add')}
                            buttonOnClick={() => setIsOpenLanguagePopup(true)}
                        >
                            {t('general.UI.languages')}
                        </Subtitle>

                        <div style={{ marginBottom: 40 }}>
                            <LanguageLevelList
                                isDark
                                languages={formik.values.languages}
                                onRemove={(index) => {
                                    const newLanguages = [...formik.values.languages];
                                    newLanguages.splice(index, 1);
                                    formik.setFieldValue('languages', newLanguages);
                                }}
                            />
                        </div>
                        {isOpenLanguagePopup && (
                            <BasicPopup
                                isOpen={isOpenLanguagePopup}
                                closePopup={() => setIsOpenLanguagePopup(false)}
                            >
                                <LanguageForm
                                    onSubmit={(values) => {
                                        const languageExists = formik.values.languages.find((language) => language.languageCode === values.languageCode);

                                        if (!languageExists) {
                                            formik.setFieldValue('languages', [...formik.values.languages, { id: Date.now(), ...values }]);
                                            setIsOpenLanguagePopup(false)
                                        }
                                    }}
                                />
                            </BasicPopup>
                        )}
                        {isOpenLogicTestPopup &&
                            <BasicPopup
                                isOpen={isOpenLogicTestPopup}
                                closePopup={() => handlePopupClose('logicTest')}
                                style={{ maxHeight: '80vh', overflowY: 'auto' }}
                            >
                                {isLogicMoreThan30Days ? (

                                    showTest ? (
                                        <LogicTestForm isDark questions={testOptions} answersToken={answersToken} callback={() => handlePopupClose('logicTest')} />
                                    ) : (
                                        <div style={{ textAlign: 'center' }}>
                                            <h6 style={{ marginBottom: 15 }}>{t('edit_profile.job_seeker.work_proficiency_test_title')}</h6>
                                            <Button type="button" onClick={handleStartTest}>
                                                {isTestLoading ? t('general.UI.loading') : t('forms.welcome_job_seeker.step_3.take_test')}
                                            </Button>
                                        </div>
                                    )

                                ) : (
                                    <h6 style={{ textAlign: 'center', marginBottom: 0 }}>{t('forms.welcome_job_seeker.step_3.test_frequency')}</h6>
                                )}
                            </BasicPopup>
                        }
                        <Button onClick={formik.submitForm} className={styles.saveButton} >
                            {formLoading ? t('general.UI.loading') : t('general.UI.save')}
                        </Button>
                    </div>
                }
            </div>

            <div className={styles.thirdColumn}>
                {!editMode ?
                    <Close
                        className={styles.crossIcon}
                        style={{
                            cursor: "pointer"
                        }}
                        onClick={() => { setEditMode(!editMode) }}
                    /> :

                    <img
                        src={EditIcon}
                        onClick={() => setEditMode(!editMode)}
                        alt={t('personalInformation.edit')}
                        className={styles.editIcon}
                    />
                }
            </div>
        </div>
    );
}

export default PersonalInformation;
